import { Entry } from 'contensis-management-api';
import clone from 'rfdc';
import { EntryBuilder } from './EntryBuilder';
import { NulledEntry } from '~/models';

/**
 * Builds an entry from a payload that only supplies some of its fields
 * (`partialEntries`): every unsupplied field is carried over from the
 * existing target entry instead of being wiped. An explicit `null` is a
 * supplied value and clears the field. Existing values are copied verbatim.
 */
export class PartialEntryBuilder extends EntryBuilder {
  /**
   * The field keys the payload entry actually carries, resolved once
   */
  private readonly suppliedKeys = this.resolveSuppliedKeys();

  /**
   * Fields the payload supplies that the target content type still has a
   * place for. Anything else keeps its existing target value.
   */
  private resolveSuppliedKeys() {
    const { contentType, sourceContentType, sourceEntry } = this;
    const targetIds = new Set((contentType?.fields || []).map((f) => f.id));
    return new Set(
      (sourceContentType?.fields || [])
        .map((f) => f.id)
        .filter(
          (id) =>
            targetIds.has(id) &&
            // an own key with an undefined value counts as absent, matching
            // JSON.stringify semantics
            Object.prototype.hasOwnProperty.call(sourceEntry, id) &&
            sourceEntry[id] !== undefined
        )
    );
  }

  /**
   * The existing target entry to merge from, or nothing to merge when there is
   * no existing entry, or the base has built a first-pass stub of the source entry
   */
  get mergeBasis(): Entry | undefined {
    const { existing } = this;
    return existing &&
      !(this.isStubContentType && !this.contentType) &&
      !this.contensis.context.isCopyOperation &&
      !this.builtContentType
      ? existing
      : undefined;
  }

  /**
   * Fill every field the payload did not supply with its existing target value
   */
  toJSON(opts = { firstPass: false }) {
    const entry = super.toJSON(opts);
    const existing = this.mergeBasis;
    if (!existing) return entry;

    const { suppliedKeys } = this;
    const { entryTitleField, entryDescriptionField, entryThumbnailField } =
      (this.contentType as any) || {};

    for (const [key, value] of Object.entries(existing)) {
      // sys is built from the source entry and merged with the target sys on update
      if (key === 'sys') continue;
      // supplied fields always win, including an explicit null
      if (suppliedKeys.has(key)) continue;
      // a derived alias goes with the field it is taken from
      if (
        (key === 'entryTitle' && suppliedKeys.has(entryTitleField)) ||
        (key === 'entryDescription' &&
          suppliedKeys.has(entryDescriptionField)) ||
        (key === 'entryThumbnail' && suppliedKeys.has(entryThumbnailField))
      )
        continue;
      entry[key] = clone()(value);
    }

    return entry;
  }

  /**
   * Only diff `sys.schedules` when the payload supplies schedules — otherwise
   * a target entry sitting on a publish schedule would make every partial
   * update look like a change.
   */
  nullFields(e: Entry): NulledEntry {
    const nulled = super.nullFields(e);
    return Object.keys((this.sourceEntry.sys as any)?.schedules || {}).length
      ? nulled
      : { ...nulled, sys: null };
  }
}
